import * as Sentry from "@sentry/react";

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const APP_VERSION = "1.0.0";
const RELEASE_NAME = `inteligencia-criativa@${APP_VERSION}`;

/**
 * Initialize Sentry for the React frontend.
 * Includes: Error tracking, Performance monitoring, Session Replay,
 * Browser tracing, and custom breadcrumbs.
 */
export function initSentry() {
  if (!SENTRY_DSN) {
    console.warn("[Sentry] DSN not configured, skipping initialization");
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE || "development",
    release: RELEASE_NAME,

    integrations: [
      Sentry.browserTracingIntegration({
        enableInp: true,
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: true,
        blockAllMedia: false,
      }),
      Sentry.breadcrumbsIntegration({
        console: true,
        dom: true,
        fetch: true,
        history: true,
        xhr: true,
      }),
      Sentry.httpClientIntegration(),
      Sentry.globalHandlersIntegration({
        onerror: true,
        onunhandledrejection: true,
      }),
    ],

    // Performance Monitoring - higher sample rate in dev
    tracesSampleRate: import.meta.env.MODE === "production" ? 0.3 : 1.0,
    tracePropagationTargets: [
      "localhost",
      /^\//,
      /\.manus\.computer/,
      /\.manus\.space/,
    ],

    // Session Replay - always capture on error, sample 10% normally
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Filter noisy errors that are not actionable
    beforeSend(event, hint) {
      const error = hint?.originalException;

      // ResizeObserver loop errors (browser noise, not real errors)
      if (error instanceof Error && error.message?.includes("ResizeObserver")) {
        return null;
      }

      // Ad blockers blocking network requests
      if (error instanceof Error && error.message?.includes("net::ERR_BLOCKED_BY_CLIENT")) {
        return null;
      }

      // Network errors from extensions
      if (error instanceof Error && error.message?.includes("Failed to fetch")) {
        // Only skip if it's not a tRPC call
        if (!event.request?.url?.includes("/api/trpc")) {
          return null;
        }
      }

      // Skip auth redirects (expected flow)
      if (error instanceof Error && error.message?.includes("10001")) {
        return null;
      }

      return event;
    },

    // Filter noisy breadcrumbs
    beforeBreadcrumb(breadcrumb) {
      // Skip debug-level console breadcrumbs
      if (breadcrumb.category === "console" && breadcrumb.level === "debug") {
        return null;
      }
      // Skip health check breadcrumbs
      if (breadcrumb.data?.url?.includes("/health")) {
        return null;
      }
      return breadcrumb;
    },

    initialScope: {
      tags: {
        component: "frontend",
        app: "inteligencia-criativa",
        platform: "doctors-group",
      },
    },
  });

  console.log(`[Sentry] Initialized (env: ${import.meta.env.MODE}, release: ${RELEASE_NAME})`);
}

/**
 * Set user context on Sentry after authentication.
 */
export function setSentryUser(user: {
  id: number | string;
  openId?: string;
  name?: string | null;
  email?: string | null;
  role?: string | null;
}) {
  Sentry.setUser({
    id: String(user.id),
    username: user.name || undefined,
    email: user.email || undefined,
    segment: user.role || "unknown",
  });
}

/**
 * Clear user context (e.g., on logout).
 */
export function clearSentryUser() {
  Sentry.setUser(null);
}

/**
 * Add a custom breadcrumb for tracking user actions.
 */
export function addBreadcrumb(
  category: string,
  message: string,
  data?: Record<string, unknown>,
  level: Sentry.SeverityLevel = "info"
) {
  Sentry.addBreadcrumb({
    category,
    message,
    data,
    level,
    timestamp: Date.now() / 1000,
  });
}

/**
 * Track page navigation as breadcrumb.
 */
export function trackNavigation(from: string, to: string) {
  addBreadcrumb("navigation", `Navigated from ${from} to ${to}`, { from, to });
}

/**
 * Track a user action (button click, form submit, etc.)
 */
export function trackAction(action: string, data?: Record<string, unknown>) {
  addBreadcrumb("user.action", action, data);
}

/**
 * Track a dashboard filter change.
 */
export function trackFilterChange(filter: string, value: unknown) {
  addBreadcrumb("filter", `Filter changed: ${filter}`, { filter, value: String(value) });
}

/**
 * Track a dashboard page view with context.
 */
export function trackDashboardView(dashboardName: string, role?: string) {
  addBreadcrumb("dashboard.view", `Viewed dashboard: ${dashboardName}`, {
    dashboard: dashboardName,
    role: role || "unknown",
  });
}

/**
 * Track a creative preview interaction.
 */
export function trackCreativePreview(adId: string, adName: string, previewType: string) {
  addBreadcrumb("creative.preview", `Previewed creative: ${adName}`, {
    adId,
    adName,
    previewType,
  });
}

/**
 * Track an AI feedback request.
 */
export function trackAiFeedback(adName: string) {
  addBreadcrumb("ai.feedback", `Requested AI feedback for: ${adName}`, { adName });
}

/**
 * Capture a custom exception with context.
 */
export function captureException(error: unknown, context?: Record<string, unknown>) {
  Sentry.withScope((scope) => {
    if (context) {
      Object.entries(context).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }
    Sentry.captureException(error);
  });
}

/**
 * Capture a custom message.
 */
export function captureMessage(message: string, level: Sentry.SeverityLevel = "info") {
  Sentry.captureMessage(message, level);
}

/**
 * Get the Sentry ErrorBoundary component for wrapping React trees.
 */
export const SentryErrorBoundary = Sentry.ErrorBoundary;

export { Sentry };
